/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box, Divider } from "@mui/material";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import WaitingForAiResponse from "@/src/components/Form/Common/WaitingForAiResponse/WaitingForAiResponse";
import { useWaitForAsyncFields } from "@/src/hooks/useWairForAsyncFields";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";
import MuiListNativeStyled from "@/src/components/List/MuiListNativeStyled";
import RefinementDocument from "@/src/components/Form/Common/AiAssessment/RefinementDocument";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";

type RefinementProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  fieldsToWaitFor?: string[];
};

export default function Refinement({ submitProps, product, fieldsToWaitFor = ["aiInitialAssessment", "aiRefinementQuestions"] }: RefinementProps) {
  const queryClient = useQueryClient();
  const hasQuestions = Boolean(product.aiRefinementQuestions);
  const refinementDocuments = product.aiRefinementDocuments ?? [];
  const hasDocuments = refinementDocuments.length > 0;

  const mediaObjectUrlParams = {
    product: product.name,
    productId: product.id,
  };
  const { data: mediaObjectCollection, isLoading: isLoadingMediaObjects } = useQuery({
    queryKey: queryKeys.collection("/media_objects", mediaObjectUrlParams),
    queryFn: () => fetchCollection("/media_objects", mediaObjectUrlParams),
    enabled: hasDocuments,
  });

  const { handleSubmit } = useForm();

  const { isWaiting: isWaitingForAiResponse } = useWaitForAsyncFields<AbstractCase>({
    product,
    requiredFields: fieldsToWaitFor,
  });

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Ihr Anliegen"}
        headline={"Wir haben Ihr Anliegen wie folgt verstanden:"}
        text={"Ihr Anliegen wird gerade analysiert und zusammengefasst. Dies kann bis zu einer Minute dauern. Bitte laden Sie die Seite nicht neu."}
      />
    );
  }
  if (hasDocuments && isLoadingMediaObjects) {
    return <LegalbirdLoader />;
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          {(hasQuestions || hasDocuments) && (
            <>
              <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
                Ihr Anliegen
              </Typography>
              <Grid
                container
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "baseline",
                }}
              >
                <Grid size={12}>
                  <FormLegend>
                    <strong>Wir haben Ihr Anliegen wie folgt verstanden:</strong>
                  </FormLegend>
                </Grid>
                {!isWaitingForAiResponse && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>{product.aiInitialAssessment}</FormLegend>
                    </Grid>
                    {hasQuestions && (
                      <>
                        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                          <AlertLegalbird severity="info">
                            <AlertContentLegalbird
                              title={"Bitte lassen Sie uns noch folgende Informationen zukommen:"}
                              description={<Box dangerouslySetInnerHTML={{ __html: product.aiRefinementQuestions }} />}
                            />
                          </AlertLegalbird>
                        </Grid>
                        <Grid size={12}>
                          <ValidatorTextField
                            name="refinementAnswers"
                            label="Ergänzende Informationen"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            multiline
                            rows={10}
                          />
                        </Grid>
                      </>
                    )}
                    {hasDocuments && (
                      <>
                        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                          <AlertLegalbird severity="info">
                            <AlertContentLegalbird
                              title={"Bitte laden Sie nach Möglichkeit noch folgende Unterlagen hoch:"}
                              description={
                                <MuiListNativeStyled listItems={refinementDocuments.map((doc) => doc.documentName + " - " + doc.description)} />
                              }
                            />
                          </AlertLegalbird>
                        </Grid>
                        {refinementDocuments.map((requestedDocument, index) => (
                          <Grid size={12} sx={wizardStyle.gridTopSpaced} key={requestedDocument.documentName}>
                            <RefinementDocument
                              requestedDocument={requestedDocument}
                              product={product}
                              index={index}
                              mediaObjectCollection={mediaObjectCollection}
                              refreshMediaObjectCollection={() =>
                                queryClient.invalidateQueries({
                                  queryKey: queryKeys.collection("/media_objects", mediaObjectUrlParams),
                                })
                              }
                            />
                            <Divider sx={{ margin: "1rem 0" }} />
                          </Grid>
                        ))}
                      </>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}
          {!hasQuestions && !hasDocuments && (
            <>
              <Typography sx={{ textAlign: "center" }}>
                <strong>Vielen Dank für Ihre Angaben.</strong>
              </Typography>
              <Typography>
                Wir benötigen derzeit weder zusätzliche Informationen noch weitere Unterlagen von Ihnen. Mit „Weiter“ gelangen Sie zur abschließenden
                Zusammenfassung Ihres Anliegens.
              </Typography>
            </>
          )}
          <FormSubmit {...submitProps} disabled={isWaitingForAiResponse} />
        </Box>
      </FormFader>
    </Paper>
  );
}
